import { updateState } from '@angular-architects/ngrx-toolkit';
import {
    signalStore,
    withComputed,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import { environment } from '@dxp/core/environment';
import {
    DefaultRingtime,
    DefaultVoiceLogin,
    DefaultVoiceServer,
    DefaultVoiceStunServers,
    DefaultWrap,
    IOperatorRingtime,
    IVoiceLogin,
    IVoiceServers,
    IVoiceStunServers,
    IWrap,
    RbacConversationSortType,
} from '@dxp/core/models';
import { inject, DestroyRef, computed } from '@angular/core';
import {
    PresenceApiService,
    RbacApiService,
    VoiceApiService,
} from '@dxp/core/api';
import {
    filter,
    map,
    distinctUntilChanged,
    switchMap,
    catchError,
    forkJoin,
    of,
    tap,
} from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { withUser, USER_STORE } from '../user';

interface ISettingsStore {
    conversationSortingMode: RbacConversationSortType;
    wrap: IWrap;
    breakNames: string[];
    isAiSummaryEnabled: boolean;
    asteriskServers: IVoiceServers;
    asteriskStunServers: IVoiceStunServers;
    asteriskLogin: IVoiceLogin;
    operator: IOperatorRingtime;
}

const initialState: ISettingsStore = {
    conversationSortingMode: 'ByQueueTime',
    wrap: DefaultWrap,
    breakNames: [],
    isAiSummaryEnabled: false,
    asteriskServers: DefaultVoiceServer,
    asteriskStunServers: DefaultVoiceStunServers,
    asteriskLogin: DefaultVoiceLogin,
    operator: DefaultRingtime,
};

export const SettingsStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('SettingsStore'),
    withFeature(() => withUser(inject(USER_STORE))),
    withState(initialState),
    withComputed(store => ({
        asteriskReady: computed<boolean>(() => {
            try {
                return (
                    store.asteriskLogin().isSuccess === true &&
                    store.asteriskServers().isSuccess === true
                );
            } catch {
                return false;
            }
        }),
        operatorRingtime: computed<number>(() => {
            const operator = store.operator();

            return operator?.isSuccess && operator.data.length > 0
                ? operator.data[0].ringTime
                : 20;
        }),
    })),
    withMethods(store => ({
        updateSettings(
            conversationSortingMode: RbacConversationSortType,
            wrap: IWrap,
            breakNames: string[],
            isAiSummaryEnabled: boolean,
        ) {
            updateState(store, 'UPDATE_SETTINGS', state => {
                return {
                    ...state,
                    conversationSortingMode: conversationSortingMode,
                    wrap: wrap,
                    breakNames: breakNames,
                    isAiSummaryEnabled: isAiSummaryEnabled,
                };
            });
        },
        updateAsteriskSettings(
            asteriskServers: IVoiceServers,
            asteriskStunServers: IVoiceStunServers,
            asteriskLogin: IVoiceLogin,
            operatorRingtime: IOperatorRingtime,
        ) {
            updateState(store, 'UPDATE_SETTINGS_ASTERISK', state => {
                return {
                    ...state,
                    asteriskServers: asteriskServers,
                    asteriskStunServers: asteriskStunServers,
                    asteriskLogin: asteriskLogin,
                    operator: operatorRingtime,
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            oidcSecurityService = inject(OidcSecurityService),
            rbacApiService = inject(RbacApiService),
            presenceApiService = inject(PresenceApiService),
            voiceApiService = inject(VoiceApiService),
            destroyRef = inject(DestroyRef),
        ) {
            toObservable(store.user)
                .pipe(
                    map(user => user.tenantId),
                    filter(id => !!id && id.length > 0),
                    distinctUntilChanged(),
                )
                .pipe(
                    switchMap(tenantId =>
                        forkJoin({
                            conversationSortingMode: rbacApiService
                                .getConversationSortingMode()
                                .pipe(
                                    map(res => res.sortingType),
                                    catchError(() => {
                                        return of<'ByQueueTime'>('ByQueueTime');
                                    }),
                                ),
                            wrap: rbacApiService.getWrapDetails().pipe(
                                catchError(() => {
                                    return of(DefaultWrap);
                                }),
                            ),
                            breakNames: presenceApiService.getBreakNames().pipe(
                                catchError(() => {
                                    return of([]);
                                }),
                            ),
                            isAiSummaryEnabled: rbacApiService
                                .getConversationSummary(tenantId)
                                .pipe(
                                    map(
                                        summary =>
                                            summary.isConversationSummaryEnabled,
                                    ),
                                    catchError(() => {
                                        return of(false);
                                    }),
                                ),
                        }),
                    ),
                    tap(
                        ({
                            conversationSortingMode,
                            wrap,
                            breakNames,
                            isAiSummaryEnabled,
                        }) => {
                            store.updateSettings(
                                conversationSortingMode,
                                wrap,
                                breakNames,
                                isAiSummaryEnabled,
                            );
                        },
                    ),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            oidcSecurityService.isAuthenticated$
                .pipe(
                    filter(result => result.isAuthenticated),
                    distinctUntilChanged(),
                    switchMap(() =>
                        forkJoin({
                            asteriskServers: voiceApiService
                                .getAsteriskServers()
                                .pipe(
                                    catchError(() => {
                                        return of(DefaultVoiceServer);
                                    }),
                                ),
                            asteriskStunServers: voiceApiService
                                .getAsteriskStunServers()
                                .pipe(
                                    catchError(() => {
                                        return of(DefaultVoiceStunServers);
                                    }),
                                ),
                            asteriskLogin: voiceApiService
                                .getAsteriskLogin()
                                .pipe(
                                    catchError(() => {
                                        return of(DefaultVoiceLogin);
                                    }),
                                ),
                            operatorRingtime: voiceApiService
                                .getOperatorRingtime()
                                .pipe(
                                    catchError(() => {
                                        return of(DefaultRingtime);
                                    }),
                                ),
                        }),
                    ),
                    tap(
                        ({
                            asteriskServers,
                            asteriskStunServers,
                            asteriskLogin,
                            operatorRingtime,
                        }) => {
                            store.updateAsteriskSettings(
                                asteriskServers,
                                asteriskStunServers,
                                asteriskLogin,
                                operatorRingtime,
                            );
                        },
                    ),
                )
                .subscribe();
        },
    }),
);
